import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { selectAnnotationState, updateAnnotationState } from '../app/annotations.js';
import {
  selectSelectedAnnotationIds,
  selectTranslateOffsetDraft,
  setAnnotationsForGeometryEdit,
  setNewGeometries,
  setTranslateOffsetDraft,
} from '../app/interface.js';
import {
  openlayer_to_reference_coordinates,
  reference_to_openlayer_coordinates,
} from '../app/mapInteractions.js';
// trigger by the Translate mode or the two-click measure
// used to enter an X/Y offset and apply it to the selection
function TranslateModal({ map, featuresLayer, onTranslateSavedRef }) {
  const dispatch = useDispatch();
  const annotationState = useSelector(selectAnnotationState);
  const selectedAnnotations = useSelector(selectSelectedAnnotationIds);
  const translateOffsetDraft = useSelector(selectTranslateOffsetDraft);
  const [translateX, setTranslateX] = useState("");
  const [translateY, setTranslateY] = useState("");

  // trigger by the two-click measure
  // used to fill the modal inputs
  useEffect(() => {
    if (!translateOffsetDraft) return;
    setTranslateX(String(translateOffsetDraft.x));
    setTranslateY(String(translateOffsetDraft.y));
    dispatch(setTranslateOffsetDraft(null));
  }, [translateOffsetDraft, dispatch]);

  // trigger by the Translate button
  // used to move the selected annotations by the X/Y offset
  const applyTranslateOffset = (x, y, onSaved) => {
    if (!map) return;
    const [dxOl, dyOl] = reference_to_openlayer_coordinates([Number(x) || 0, Number(y) || 0], map);
    const layerFeatures = featuresLayer?.getSource?.()?.getFeatures?.() || [];
    const features = selectedAnnotations
      .map((id) => layerFeatures.find((f) => f.getProperties()?.annotation?.id === id))
      .filter(Boolean);
    if (!features.length) return;
    onTranslateSavedRef.current = onSaved || null;

    const saves = features.map((feature) => {
      const annotation = feature.getProperties().annotation;
      const geometry = feature.getGeometry();
      geometry.translate(dxOl, dyOl);
      const type = geometry.getType().toLowerCase();
      const coordinates = geometry.getCoordinates();
      return {
        id: annotation.id,
        geometry: {
          type: geometry.getType(),
          coordinates: type === 'point'
            ? openlayer_to_reference_coordinates(coordinates, map)
            : type === 'polygon'
              ? coordinates[0].map((c) => openlayer_to_reference_coordinates(c, map))
              : coordinates.map((c) => openlayer_to_reference_coordinates(c, map)),
        },
        geometryName: type === 'linestring' ? 'line' : type,
      };
    });
    dispatch(setAnnotationsForGeometryEdit(saves.map((s) => s.id)));
    dispatch(setNewGeometries(saves.map(({ geometry, geometryName }) => ({ geometry, geometryName }))));
  };

  return (
    <Dialog
      open={annotationState === "AnnotationsTranslateModalOnGoing"}
      onClose={() => dispatch(updateAnnotationState("SelectionOnGoing"))}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Translate selection</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="X"
            type="number"
            value={translateX}
            onChange={(e) => setTranslateX(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Y"
            type="number"
            value={translateY}
            onChange={(e) => setTranslateY(e.target.value)}
            fullWidth
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3, pb: 2 }}>
        <Button
          variant="contained"
          disabled={selectedAnnotations.length === 0 || (translateX === "" && translateY === "")}
          onClick={() => {
            // on callback, reset the inputs
            applyTranslateOffset(translateX, translateY, () => {
              setTranslateX("");
              setTranslateY("");
            });
            dispatch(updateAnnotationState("SelectionOnGoing"));
          }}
        >
          Translate
        </Button>
        <Button
          variant="outlined"
          onClick={() => dispatch(updateAnnotationState("AnnotationsTranslateByTwoClicksOnGoing"))}
        >
          Translate with two clicks
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TranslateModal;
